import React, { useEffect, useState } from "react";
import "./index.scss";
import { Grid, IconButton, Tooltip, Typography, Box } from "@mui/material";
import CalenderIcon from "../../../resources/icons/calender-icon.svg";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { FY_MONTH } from "../../../components/financial-year-calendar/helper";
import { convertToCamelCaseFromUnderScore } from "../../../utils";
import moment from "moment/moment";
import SiTextField from "../../../core/si-textfield";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { HOSTNAME, REST_URLS } from "../../../utils/endpoints";
import { Icon } from "@iconify/react";
import CustomButtonComponent from "../../../core/custom-button";
const EngineerMonthlyCalender = ({ setIsShowCalender }) => {
  const [engineersData, setEngineerData] = useState([]);
  const monthName = new Date().toLocaleString("default", { month: "long" });
  const year = new Date().getFullYear();
  const [calenderYear, setCalenderYear] = useState("");
  const selectedMonthIndex = FY_MONTH.indexOf(monthName);
  const actualMonthIndex = ((selectedMonthIndex + 3) % 12) + 1;
  const displayYear = year;
  const [initialDate] = useState(
    `${displayYear}-${String(actualMonthIndex).padStart(2, "0")}-01`
  );
  const [eventList, setEventList] = useState([]);
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [formattedData, setFormattedData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  // Helper to transform attendance data into calendar event format
  const getEventsFromAttendance = (attendance, year) => {
    const events = [];
    Object.entries(attendance || {}).forEach(([month, days]) => {
      Object.entries(days).forEach(([dayKey, details]) => {
        if (dayKey.includes("-")) {
          // Handle range like "28-30"
          const [startDay, endDay] = dayKey.split("-").map(Number);

          // Add one day to the end date to make it inclusive in FullCalendar
          const adjustedEndDay = endDay + 1;

          const startDate = `${year}-${String(month).padStart(2, "0")}-${String(
            startDay
          ).padStart(2, "0")}`;

          const endDate = `${year}-${String(month).padStart(2, "0")}-${String(
            adjustedEndDay
          ).padStart(2, "0")}`;

          events.push({
            title: details?.status,
            start: startDate,
            end: endDate, // Exclusive end, so +1 day adjustment is necessary
            extendedProps: {
              reason: details?.reason,
              className: "custom-event-class",
            },
          });
        } else {
          // Handle single day like "27"
          const eventDate = `${year}-${String(month).padStart(2, "0")}-${String(
            dayKey
          ).padStart(2, "0")}`;
          events.push({
            title: details?.status,
            start: eventDate,
            extendedProps: {
              reason: details.reason,
              className: "custom-event-class",
            },
          });
        }
      });
    });
    return events;
  };

  // When an engineer is selected, update the calendar events
  const handleEngineerSelect = (engineer) => {
    setSelectedEngineer(engineer);
    const events = getEventsFromAttendance(engineer.attendance, engineer.year);
    setEventList(events);
  };

  function modifyData(data) {
    return data.map((userData) => {
      const modifiedAttendance = {};

      for (const monthKey in userData.attendance) {
        const monthAttendance = userData.attendance[monthKey];
        const dayKeys = Object.keys(monthAttendance)
          .filter((dayKey) => !isNaN(dayKey)) // Ensure keys are valid numbers (day keys)
          .map((day) => parseInt(day, 10)) // Convert keys to integers for sorting
          .sort((a, b) => a - b); // Sort day keys numerically

        let groupedAttendance = {}; // Stores grouped days for the current month
        let startDay = null; // Start day of the current group
        let prevDay = null; // Previous day in the loop
        let prevStatus = null; // Previous status

        dayKeys.forEach((dayKey, index) => {
          const currentDayAttendance = monthAttendance[dayKey];
          const currentStatus = currentDayAttendance?.status;
          const isConsecutive = prevDay !== null && dayKey === prevDay + 1; // Check if days are consecutive
          const isLastDay = index === dayKeys.length - 1;

          if (!isConsecutive || currentStatus !== prevStatus) {
            // If there's an active group, finalize it
            if (startDay !== null) {
              if (startDay === prevDay) {
                groupedAttendance[startDay] = { ...monthAttendance[startDay] };
              } else {
                groupedAttendance[`${startDay}-${prevDay}`] = {
                  ...monthAttendance[startDay],
                };
              }
            }
            // Start a new group
            startDay = dayKey;
          }

          // If it's the last day, finalize the group
          if (isLastDay) {
            if (startDay === dayKey) {
              groupedAttendance[dayKey] = { ...monthAttendance[dayKey] };
            } else {
              groupedAttendance[`${startDay}-${dayKey}`] = {
                ...monthAttendance[startDay],
              };
            }
          }

          // Update the previous day and status for the next iteration
          prevDay = dayKey;
          prevStatus = currentStatus;
        });

        // Assign grouped attendance to the modified attendance
        modifiedAttendance[monthKey] = groupedAttendance;
      }

      // Return the modified user data
      return {
        ...userData,
        attendance: modifiedAttendance,
      };
    });
  }
  // Render custom event tooltip
  const renderEventContent = (eventInfo) => {
    const reason =
      eventInfo.event.title === "IDLE"
        ? "At base location"
        : eventInfo.event.extendedProps.reason;

    return (
      <Tooltip title={`Reason: ${reason}`}>
        <div
          className={`custom-event ${eventInfo.event.extendedProps.className}`}
        >
          <i>{convertToCamelCaseFromUnderScore(eventInfo.event.title)}</i>
        </div>
      </Tooltip>
    );
  };
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };
  const filteredData = formattedData.filter((engineer) =>
    engineer.user.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const loadData = (filters) => {
    const payload = { ...filters };
    invokeApi(
      HTTP_METHODS.GET,
      `${HOSTNAME}${REST_URLS.ENGINEER_MOVEMENT}`,
      null,
      payload,
      filters?.download === "TRUE" ? "blob" : null
    )
      .then((response) => {
        if (!response.message) {
          if (filters?.download === "TRUE") {
            const url = window.URL.createObjectURL(response);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Engineer Report.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
          } else {
            setEngineerData(response);
          }
        }
      })
      .catch((error) => console.error(error));
  };
  const handleDatesSet = (info) => {
    const currentYear = info?.view?.getCurrentData()?.viewTitle?.split(" ")[1];

    if (currentYear) {
      loadData({ year: parseInt(currentYear) });
    }
    setCalenderYear(parseInt(currentYear));
  };
  const downloadEngineerReport = (user = null) => {
    if (user) {
      loadData({
        year: parseInt(calenderYear),
        download: "TRUE",
        user: user.id,
      });
    } else {
      loadData({ download: "TRUE", year: parseInt(calenderYear) });
    }
  };
  const downloadServiceEngineerReport = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${HOSTNAME}${REST_URLS.SERVICE_ENG_REPORT}`,
      null,
      {
        download: "TRUE",
      },
      "blob"
    )
      .then((response) => {
        if (response) {
          const url = window.URL.createObjectURL(response);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `Service Engineer Report.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      })
      .catch((err) => console.log(err));
  };
  useEffect(() => {
    const processedData = modifyData(engineersData);
    setFormattedData(processedData);
  }, [engineersData]);
  return (
    <div className="page-body">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div className="view-detailed-ticket">
          <IconButton onClick={() => setIsShowCalender(false)}>
            <ArrowBackIcon />
          </IconButton>
          <div>
            <p className="tab-name">Engineers Movement</p>
            <p className="section-name">Select an Engineer to View Schedule</p>
          </div>
        </div>
        <div>
          <CustomButtonComponent
            variant="contained"
            color="primary"
            onClick={() => downloadServiceEngineerReport()}
            sx={{ mr: 2 }}
          >
            Download Engineer Report
          </CustomButtonComponent>
          {engineersData.length > 0 && (
            <CustomButtonComponent
              variant="contained"
              color="success"
              onClick={() => downloadEngineerReport()}
            >
              Download Engineer Movement Report
            </CustomButtonComponent>
          )}
        </div>
      </Box>

      <Grid container spacing={2}>
        <Grid item md={9}>
          {/* Calendar View */}
          <div className="month-view-container" style={{ width: "auto" }}>
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              initialDate={initialDate}
              events={eventList}
              eventContent={renderEventContent}
              datesSet={handleDatesSet}
            />
          </div>
        </Grid>
        <Grid item md={3}>
          {/* Engineer List */}
          <div
            className="form-con"
            style={{ maxHeight: "80vh", overflow: "auto" }}
          >
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  <Typography>Details Day</Typography>
                  <Typography variant="body2" color="gray" sx={{ mt: 1 }}>
                    Engineer's details
                  </Typography>
                </div>
                {filteredData.length > 0 && (
                  <SiTextField
                    variant="outlined"
                    size="small"
                    placeholder="Search Engineer"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    sx={{ mb: 2, width: "50%", mr: 1 }}
                  />
                )}
              </Box>

              {filteredData.length ? (
                filteredData?.map((engineersData, index) => {
                  const { user, attendance } = engineersData;
                  let status = "No Data"; // Default status if no record found
                  let reason = "";
                  let dateDisplay = ""; // To store the date or range to display
                  const today = new Date();
                  const todayYear = today.getFullYear();
                  const todayMonth = String(today.getMonth() + 1).padStart(
                    2,
                    "0"
                  ); // Months are zero-based
                  const todayDay = today.getDate();
                  // Check attendance for the current month and day
                  if (attendance && attendance[todayMonth]) {
                    Object.entries(attendance[todayMonth]).forEach(
                      ([dayKey, details]) => {
                        if (dayKey.includes("-")) {
                          // Handle ranges like "28-30"
                          const [startDay, endDay] = dayKey
                            .split("-")
                            .map(Number);
                          if (todayDay >= startDay && todayDay <= endDay) {
                            status = details?.status;
                            reason = details?.reason;
                            dateDisplay = `from ${moment(
                              `${todayYear}-${todayMonth}-${String(
                                startDay
                              ).padStart(2, "0")}`
                            ).format("DD MMM")}  to ${moment(
                              `${todayYear}-${todayMonth}-${String(
                                endDay
                              ).padStart(2, "0")}`
                            ).format("DD MMM")}`;
                          }
                        } else if (Number(dayKey) === todayDay) {
                          // Handle single day
                          status = details?.status;
                          reason = details?.reason;
                          dateDisplay = moment(
                            `${todayYear}-${todayMonth}-${String(
                              todayDay
                            ).padStart(2, "0")}`
                          ).format("DD MMM");
                        }
                      }
                    );
                  }

                  return (
                    <div
                      key={index + 1}
                      style={{ marginBottom: "16px" }}
                      className={`movement-con ${
                        selectedEngineer?.id === engineersData?.id
                          ? "selected-engineer"
                          : ""
                      }`}
                      onClick={() => handleEngineerSelect(engineersData)}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body1">{user.name}</Typography>
                        <IconButton
                          onClick={() => downloadEngineerReport(user)}
                          sx={{ padding: "0px" }}
                        >
                          <Icon
                            icon="solar:download-bold-duotone"
                            width="1.5rem"
                            height="1.5rem"
                            style={{ color: "green" }}
                          />
                        </IconButton>
                      </Box>

                      <Typography variant="body2" color="gray" sx={{ mt: 0.5 }}>
                        Reason: {reason || "NA"}
                      </Typography>

                      {dateDisplay && (
                        <Typography
                          variant="body1"
                          sx={{ mt: 1 }}
                          color="gray"
                          fontWeight={600}
                        >
                          <img
                            src={CalenderIcon}
                            alt="calender-icon"
                            style={{ marginRight: 5 }}
                          />{" "}
                          {dateDisplay}
                        </Typography>
                      )}
                    </div>
                  );
                })
              ) : (
                <Typography sx={{ textAlign: "center", mt: 2, mb: 2 }}>
                  No Record
                </Typography>
              )}
            </Box>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default EngineerMonthlyCalender;
